/**
 * How exercises on a sheet are numbered: `TB.3` is prefix `T`, sheet number `B`,
 * exercise 3. Shared by the editor, the new-sheet form's "will be numbered" line and the
 * outline, which previously each had to agree with a copy inside the editor.
 */
import { courseFunctionFor, headerForSheet, isCourseSheet, type CourseFunction, type SheetDoc, type SheetItem, type SheetMeta } from '@studio/shared';
import { tx } from './i18n';

/*
 * The letter each 2026 course template sets (`sheet-type.update(…)` in the course's
 * `templates.typ`). A course sheet ignores `meta.sheetType` — the template decides — so
 * the editor has to print the template's letter to agree with the PDF.
 */
const COURSE_PREFIX: Record<CourseFunction, string> = {
  'tutorial-sheet': 'T',
  'exercise-sheet': 'E',
  'exam-preparation-sheet': 'P',
  'extra-tutorial-sheet': 'A',
  'rep-tutorial-sheet': 'REP',
  'exam-task-sheet': 'EXT',
  'explanation-sheet': 'X',
  'knobel-sheet': 'K',
};

type PrefixMeta = Pick<SheetMeta, 'sheetType' | 'kind'> &
  Partial<Pick<SheetMeta, 'headerName' | 'courseFunction' | 'headerYear'>>;

/*
 * The `\sheettype` each LaTeX header pack sets, as `/api/headers` lists it (`year/name`
 * → letter). A sheet with no `meta.sheetType` of its own prints its pack's letter — a
 * copy of a 2023 exercise sheet prints `E0.1` — so the editor has to know the pack's
 * letter too, or it numbered those exercises `T0.1`. Filled by `useHeaders`; until it
 * has loaded the old guess stands.
 */
const packSheetTypes = new Map<string, string>();
const yearSheetTypes = new Map<string, string>();

export function learnPackSheetTypes(packs: readonly { name: string; year: string; sheetType: string | null }[]): void {
  for (const p of packs) {
    if (p.sheetType === null || p.sheetType === undefined) continue;
    packSheetTypes.set(`${p.year}/${p.name}`, p.sheetType);
    // the server's fallback: `\sheettype` set by a sibling fragment of the same year
    if (!yearSheetTypes.has(p.year)) yearSheetTypes.set(p.year, p.sheetType);
  }
}

/** The pack's own letter for a sheet without an override, as the build resolves it. */
function packSheetType(meta: PrefixMeta): string | undefined {
  if (!meta.headerYear) return undefined;
  const name = headerForSheet(meta as SheetMeta);
  return packSheetTypes.get(`${meta.headerYear}/${name}`) ?? yearSheetTypes.get(meta.headerYear);
}

/** Typst's `numbering("A", n)`: 1 → A, 26 → Z, 27 → AA. */
function letterNumber(n: number): string {
  let out = '';
  for (let k = n; k > 0; k = Math.floor((k - 1) / 26)) out = String.fromCharCode(65 + ((k - 1) % 26)) + out;
  return out;
}

/*
 * The sheet number between the prefix and the exercise counter, as the PDF prints it.
 *
 * Every LaTeX head and most course templates print the label as typed. Two course
 * templates do not: `explanation-sheet` takes no number at all, so its exercises read
 * `X.1`, and `exam-task-sheet` prints `numbering("A", n)` of the integer the generator
 * passes — a letter label is that letter, a digit label `2` becomes `B`, anything else
 * falls back to 1 (`A`), the same rule as `buildCoursePlan`'s number argument.
 */
export function sheetNumber(meta: PrefixMeta & Pick<SheetMeta, 'label'>): string {
  const label = meta.label ?? '';
  if (!isCourseSheet(meta)) return label;
  const fn = courseFunctionFor(meta as SheetMeta);
  if (fn === 'explanation-sheet') return '';
  if (fn === 'exam-task-sheet') {
    const text = label.trim();
    if (/^[A-Za-z]$/.test(text)) return text.toUpperCase();
    if (/^[1-9]\d{0,5}$/.test(text)) return letterNumber(Number(text));
    return 'A';
  }
  return label.trim();
}

export function sheetTypePrefix(meta: PrefixMeta): string {
  if (isCourseSheet(meta)) return COURSE_PREFIX[courseFunctionFor(meta as SheetMeta)];
  if (meta.sheetType !== undefined && meta.sheetType !== null) return meta.sheetType;
  const fromPack = packSheetType(meta);
  if (fromPack !== undefined) return fromPack;
  return meta.kind === 'mock-exam' ? '' : 'T';
}

/** The number item `index` prints, honouring overrides and the starting offset. */
export function printedNumber(doc: SheetDoc, index: number): string {
  const prefix = sheetTypePrefix(doc.meta) + sheetNumber(doc.meta);
  const course = isCourseSheet(doc.meta);
  let counter = doc.meta.numberOffset ?? 0;
  for (let i = 0; i <= index; i++) {
    const item = doc.items[i];
    if (!item || item.kind !== 'exercise') continue;
    /*
     * The course template can only restart its counter, so there an override is either
     * a plain number (the counter jumps to it) or ignored, and the exercise keeps its
     * automatic number — which the build also warns about.
     */
    if (course) {
      const n = /^\s*(\d+)\s*$/.exec(item.numberOverride ?? '');
      counter = n && Number(n[1]) >= 1 ? Number(n[1]) : counter + 1;
      if (i === index) return `${prefix}.${counter}`;
      continue;
    }
    if (item.numberOverride) {
      if (i === index) return item.numberOverride;
      const trailing = /(\d+)\s*$/.exec(item.numberOverride);
      if (trailing) counter = Number(trailing[1]);
      continue;
    }
    counter += 1;
    if (i === index) return `${prefix}.${counter}`;
  }
  return '';
}

/**
 * "Exercise T1.2 · Recursion", "Text block", "Heading · Warm-up" — which item a problem is
 * on, for lists that name the item beside what went wrong (the build's diagnostics, the
 * accessible PDF's refusals). `titleOf` gives an exercise's title where the caller knows
 * it; without one only a title set on the sheet is shown.
 */
export function itemName(
  doc: SheetDoc,
  itemId: string | null | undefined,
  titleOf?: (item: SheetItem) => string | null | undefined,
): string | null {
  if (!itemId) return null;
  const index = doc.items.findIndex((i) => i.id === itemId);
  if (index < 0) return null;
  const item = doc.items[index]!;
  if (item.kind === 'exercise') {
    const number = printedNumber(doc, index);
    const title = titleOf?.(item);
    return [number ? tx('Exercise {number}', { number }) : tx('Exercise'), title ? `· ${title}` : ''].filter(Boolean).join(' ');
  }
  if (item.kind === 'heading') return tx('Heading · {text}', { text: (item.body ?? '').trim().slice(0, 60) });
  if (item.kind === 'text') return tx('Text block');
  return tx('Page break');
}

/** "TB.1, TB.2, …" for a sheet that has not been made yet. */
export function numberingExample(meta: PrefixMeta & Pick<SheetMeta, 'label'>, firstNumber = 1): string {
  // an empty label shows as `?` until one is typed — unless the template prints none
  const printsNone = isCourseSheet(meta) && courseFunctionFor(meta as SheetMeta) === 'explanation-sheet';
  const prefix = sheetTypePrefix(meta) + (sheetNumber(meta) || (printsNone ? '' : '?'));
  return `${prefix}.${firstNumber}, ${prefix}.${firstNumber + 1}, …`;
}
